import React, { useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import EditModalFooter from './EditModalFooter'
import { PatchAttractionItemViewModel } from './models'
import {
  extractEditableStartDateTimeStrings,
  updateAttractionPayloadFromViewModel,
} from './utils'
import CustomThingToDoFields from '../Common/CustomThingToDoFields'
import { formOptions } from '../Common/Fields/config'
import DeleteTripItem from '../DeleteTripItem'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { usePatchTripItem } from 'tripPlanner/hooks/api/tripItem'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { AttractionItem } from 'tripPlanner/types/tripItem'

interface Props {
  item: AttractionItem
}

const itemToView = (item: AttractionItem): PatchAttractionItemViewModel => {
  return {
    type: item.type,
    name: item.name,
    price: item.price,
    url: item.url,
    isBooked: item.isBooked,
    notes: item.notes,
    ...extractEditableStartDateTimeStrings(item),
    place: item.place,
  }
}

function EditAttraction(props: Props) {
  const { item } = props

  const openModal = useOpenModal()
  const { closeModal } = useModalCallbacks()

  const methods = useForm<PatchAttractionItemViewModel>({
    ...formOptions,
    defaultValues: itemToView(item),
  })
  const { handleSubmit } = methods

  const { mutate: updateMutate, isLoading } = usePatchTripItem({
    onSuccess: () => {
      closeModal()
    },
  })

  const deleteItem = useCallback(
    () => openModal(DeleteTripItem, { item }, { height: 'auto' }),
    [openModal, item],
  )

  const updateItem = useCallback(
    ({ ...viewModel }: PatchAttractionItemViewModel) => {
      const data = updateAttractionPayloadFromViewModel(viewModel)

      updateMutate({
        tripId: item.tripId,
        tripItemId: item.id,
        tripItem: data,
      })
    },
    [item.id, item.tripId, updateMutate],
  )

  return (
    <>
      <ModalHeader title="Edit attraction" onCloseButtonClick={closeModal} />
      <ModalBody>
        <FormProvider {...methods}>
          <ModalContent>
            <CustomThingToDoFields tripId={item.tripId} />
          </ModalContent>
        </FormProvider>
      </ModalBody>
      <EditModalFooter
        deleteItem={deleteItem}
        onSubmit={handleSubmit(updateItem)}
        isLoading={isLoading}
      />
      {isLoading && <LoadingIndicator floating />}
    </>
  )
}

export default EditAttraction
